import {isFeatureEnabled} from '@github-ui/feature-flags'
import {copilotLocalStorage} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
// eslint-disable-next-line no-restricted-imports -- This shared loader runs outside ReportErrorContextProvider trees.
import {reportError} from '@github-ui/failbot'
import {useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction} from 'react'
import type {
  AhpProvisioningTarget,
  DirectTurnTelemetrySeed,
  FirstTurnHandoff,
  PreparedPrompt,
} from '@github-ui/ahp-session-client/contracts'
import type {CopilotCustomInstructions} from '@github-ui/copilot-chat-types'
import {skipToken, useQuery} from '@github-ui/react-query'

import {isEnvironmentSelectionStale} from './environment-strip/environment-availability'
import {
  CLOUD_ENVIRONMENT_OPTION,
  SANDBOX_ENVIRONMENT_ID,
  SANDBOX_ENVIRONMENT_OPTION,
  type EnvironmentOption,
} from './environment-strip/environment-option'
import {AHP_OPTION_PREFIX, buildAhpOptions} from './environment-strip/ahp-options'
import type {Environment} from '@github-ui/agent-sessions-types/environment'
import type {McpPermissionMode} from '@github-ui/agent-sessions-types/mcp-permission-mode'
import type {AgentMode} from '@github-ui/copilot-shared-ui/utils/agent-mode'
import type {SessionApprovalMode} from '@github-ui/ahp-relay/permission-mode'
import {useEnvironments} from '@github-ui/copilot-shared-ui/environments/use-environments'
import {fetchRepo} from '@github-ui/copilot-shared-ui/repos/fetch-copilot-chat-repo'

export type EnvironmentSubmitInput = {
  environmentId: string
  prompt: PreparedPrompt
  customAgent?: {slug: string; displayName?: string}
  /** Instruction context only; does not select a repository destination. */
  repositoryInstructions?: CopilotCustomInstructions[]
  agentMode?: AgentMode
  approvalMode?: SessionApprovalMode
  repositoryNwo?: string
  baseRef?: string
  managedSandbox?: boolean
  /** Whether this environment submission is the composer's Chat launch. */
  chatLaunch?: boolean
  /**
   * MCP permission mode to seed on the created session's first turn. Only
   * carried by the Sandbox composer today; the coding-agent API path uses its
   * own `permission_mode` field on `useCreateAPITask`.
   */
  mcpPermissionMode?: McpPermissionMode
  /** Host-specific first-turn configuration, used by established Chat surfaces. */
  configureSession?: FirstTurnHandoff['configureSession']
  /** The caller or its handoff presents the failure instead of using the shared task-failure banner. */
  failureOwner?: 'caller'
  provisioningTarget?: AhpProvisioningTarget
  scopeLogin?: string
  space?: {owner: string; slug: string}
  directTurnTelemetrySeed?: DirectTurnTelemetrySeed
}

export type EnvironmentSubmitResult = {status: 'submitted'; ownsPromptDraft: boolean} | {status: 'failure-consumed'}

export type EnvironmentSubmit = (input: EnvironmentSubmitInput) => Promise<EnvironmentSubmitResult | void>

type UseEnvironmentSelectionOptions = {
  onEnvironmentSubmit?: EnvironmentSubmit
  supportsManagedSandbox?: boolean
  initialEnvironment?: EnvironmentOption | null
  /** Whether a saved AHP host should be selected when the environments list loads. */
  hydratePersistedAhp?: boolean
  /**
   * The host records the turn for a destination that provisions it later rather
   * than binding an environment itself. Such a host still composes against
   * Sandbox, so its selection is kept even though it has no environment submit
   * and is not on the environments rollout that binding one needs.
   */
  deliversByHandoff?: boolean
  /** Keeps a Sandbox selection while repository policy is loading. */
  sandboxPolicyPending?: boolean
}

type UseEnvironmentSelectionResult = {
  selectedEnvironment: EnvironmentOption | null
  setSelectedEnvironment: Dispatch<SetStateAction<EnvironmentOption | null>>
  availableEnvironments: Environment[] | undefined
  isAhpEnvironment: boolean
  isSandboxEnvironment: boolean
  showSandboxOption: boolean
  showEnvironmentPicker: boolean
  isEnvironmentsEnabled: boolean
  isSandboxPolicyPending: boolean
}

export function isSandboxOptionAvailable({
  isEnvironmentsEnabled,
  isManagedSandboxEnabled,
  supportsManagedSandbox,
  hasEnvironmentSubmit,
}: {
  isEnvironmentsEnabled: boolean
  isManagedSandboxEnabled: boolean
  supportsManagedSandbox: boolean
  hasEnvironmentSubmit: boolean
}): boolean {
  return isEnvironmentsEnabled && isManagedSandboxEnabled && supportsManagedSandbox && hasEnvironmentSubmit
}

async function fetchRepositorySandboxEnabled(repositoryId: number): Promise<boolean> {
  try {
    const repository = await fetchRepo(repositoryId)
    return repository.sandboxEnabled !== false
  } catch (error) {
    reportError(error, {message: 'Failed to fetch repository Sandbox policy'})
    return false
  }
}

export function useRepositorySandboxEnabled(repositoryId: number | undefined): boolean | undefined {
  const {data, isFetching} = useQuery({
    queryKey: ['repository-sandbox-policy', repositoryId],
    queryFn: repositoryId === undefined ? skipToken : () => fetchRepositorySandboxEnabled(repositoryId),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  return repositoryId === undefined || isFetching ? undefined : data
}

export function useSelectedRepositorySandboxPolicy({
  selectedRepositoryId,
  initialRepositoryId,
  supportsManagedSandbox,
}: {
  selectedRepositoryId: number | undefined
  initialRepositoryId: number | undefined
  supportsManagedSandbox: boolean
}): {supportsSandbox: boolean; isPending: boolean} {
  // Once the picker has owned the selection, `undefined` means explicitly cleared;
  // do not restore the mount-time repository.
  const [hasSelectedRepository, setHasSelectedRepository] = useState(false)
  if (selectedRepositoryId !== undefined && !hasSelectedRepository) setHasSelectedRepository(true)
  const repositoryId = selectedRepositoryId ?? (hasSelectedRepository ? undefined : initialRepositoryId)
  const repositorySandboxEnabled = useRepositorySandboxEnabled(repositoryId)

  if (repositoryId === undefined) return {supportsSandbox: supportsManagedSandbox, isPending: false}
  return {supportsSandbox: repositorySandboxEnabled === true, isPending: repositorySandboxEnabled === undefined}
}

/**
 * Whether the draft is being composed against Sandbox.
 *
 * Chat and the organization scope both belong to Sandbox: Actions provisions a
 * repository-backed task and offers neither. A `handoff` delivery does not
 * provision here — it records the turn for a destination that will — so it does
 * not need the environments rollout that *binding* an environment requires.
 * It is still a Sandbox draft, and must follow the Sandbox option exactly as a
 * provisioned one does; otherwise Chat would survive a switch to Actions, where
 * it cannot be started.
 */
export function isSandboxComposedDraft({
  isSandboxEnvironment,
  selectedEnvironmentKind,
  deliversByHandoff,
}: {
  isSandboxEnvironment: boolean
  selectedEnvironmentKind: EnvironmentOption['kind'] | undefined
  deliversByHandoff: boolean
}): boolean {
  return isSandboxEnvironment || (deliversByHandoff && selectedEnvironmentKind === 'sandbox')
}

/**
 * Whether the custom agent picker applies to the selected repository.
 *
 * Cloud tasks send the selection to the task API. Relay-backed environments
 * resolve the same repository agent against the host's AHP customizations
 * before dispatching the opening turn.
 */
export function isCustomAgentPickerAvailable({
  isSubAgentsEnabled,
  hasSelectedRepository,
}: {
  isSubAgentsEnabled: boolean
  hasSelectedRepository: boolean
}): boolean {
  return isSubAgentsEnabled && hasSelectedRepository
}

/**
 * Whether the toolbar's image shortcut is redundant with the draft's own attach
 * menu.
 *
 * The shortcut uploads images against the selected repository. Once a Sandbox
 * draft has one, that is the same action the attach menu already offers as a
 * local file, so only the menu is kept. A Sandbox draft with no repository has
 * no upload target for it either way, and every other environment keeps it.
 */
export function isImageAttachShortcutRedundant({
  isSandboxEnvironment,
  hasSelectedRepository,
}: {
  isSandboxEnvironment: boolean
  hasSelectedRepository: boolean
}): boolean {
  return isSandboxEnvironment && hasSelectedRepository
}

/**
 * Whether the premium-request meter applies to the draft's environment.
 *
 * A Sandbox session spends the same premium quota whichever destination the
 * draft is pointed at, so the meter belongs on Sandbox Chat and a Sandbox
 * repository task alike — hiding it on one of them tells the user their
 * remaining requests stopped mattering when they picked a repository. Actions
 * bills a background task elsewhere, so the meter would be reporting a quota
 * that destination does not draw on.
 */
export function isSessionTokenMeterApplicable({
  isTokenBasedBillingEnabled,
  isSandboxEnvironment,
}: {
  isTokenBasedBillingEnabled: boolean
  isSandboxEnvironment: boolean
}): boolean {
  return isTokenBasedBillingEnabled && isSandboxEnvironment
}

/**
 * The environments a host can actually start a session in.
 *
 * A host that creates no tasks runs exactly one thing: a Sandbox session.
 * Listing Actions there would offer a destination whose submit does not exist,
 * so the option is withheld rather than shown and then refused. The picker is
 * still rendered, because which environment the session runs in is precisely
 * what the strip is there to say — and Sandbox is also what makes Chat, the
 * organization scope, and their shared rules apply.
 *
 * Returns `undefined` for a host with a task destination, leaving the picker on
 * its own defaults.
 */
export function resolveEnvironmentOptions({
  hasTaskDestination,
}: {
  hasTaskDestination: boolean
}): EnvironmentOption[] | undefined {
  return hasTaskDestination ? undefined : [SANDBOX_ENVIRONMENT_OPTION]
}

export function useEnvironmentSelection({
  onEnvironmentSubmit,
  supportsManagedSandbox = false,
  initialEnvironment = null,
  hydratePersistedAhp = true,
  deliversByHandoff = false,
  sandboxPolicyPending = false,
}: UseEnvironmentSelectionOptions): UseEnvironmentSelectionResult {
  const isEnvironmentsEnabled = isFeatureEnabled('copilot_mission_control_environments_web_kickoff')
  const isManagedSandboxEnabled = isFeatureEnabled('copilot_mission_control_managed_sandbox_environments')
  const showSandboxOption = isSandboxOptionAvailable({
    isEnvironmentsEnabled,
    isManagedSandboxEnabled,
    supportsManagedSandbox,
    hasEnvironmentSubmit: !!onEnvironmentSubmit,
  })
  // Whether a Sandbox selection may stand. The picker offers Sandbox only when
  // the host can bind it, but a handoff host composes on Sandbox without ever
  // binding it, so dropping its selection would leave the draft on nothing.
  const canSelectSandbox = showSandboxOption || deliversByHandoff
  const canHoldSandbox = canSelectSandbox || sandboxPolicyPending
  const [selectedEnvironment, setSelectedEnvironmentState] = useState<EnvironmentOption | null>(() =>
    initialEnvironment?.kind === 'sandbox' && !canHoldSandbox ? null : initialEnvironment,
  )
  const isAutomaticInitialSandboxRef = useRef(initialEnvironment?.kind === 'sandbox')
  const setSelectedEnvironment = useCallback<Dispatch<SetStateAction<EnvironmentOption | null>>>(value => {
    isAutomaticInitialSandboxRef.current = false
    setSelectedEnvironmentState(value)
  }, [])
  const {data: availableEnvironments} = useEnvironments({enabled: isEnvironmentsEnabled && !!onEnvironmentSubmit})

  useEffect(() => {
    if (selectedEnvironment || initialEnvironment?.kind !== 'sandbox' || !canSelectSandbox) return
    // A delayed launch default is not an explicit choice and must not be persisted.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- applies an initial default after policy resolution.
    setSelectedEnvironmentState(SANDBOX_ENVIRONMENT_OPTION)
  }, [canSelectSandbox, initialEnvironment, selectedEnvironment])

  // AHP ids can't resolve synchronously because the environments list is
  // fetched. Once it arrives, hydrate a persisted AHP selection if it is
  // online and version-supported.
  useEffect(() => {
    if (!hydratePersistedAhp) return
    if (selectedEnvironment) return
    if (!availableEnvironments) return
    const persistedId = copilotLocalStorage.getLastSelectedEnvironmentId()
    if (!persistedId || !persistedId.startsWith(AHP_OPTION_PREFIX)) return
    const option = buildAhpOptions(availableEnvironments).find(env => env.id === persistedId && !env.disabled)
    // eslint-disable-next-line react-hooks/set-state-in-effect -- hydrating a persisted AHP selection once its options resolve.
    if (option) setSelectedEnvironment(option)
  }, [availableEnvironments, hydratePersistedAhp, selectedEnvironment, setSelectedEnvironment])

  // Persist the current selection after commit so a discarded render (Strict
  // Mode double-invoke, concurrent rendering) cannot push storage ahead of the
  // UI. The ref skips the initial mount so a seeded selection does not
  // overwrite storage on load.
  const persistedIdRef = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    const nextId = selectedEnvironment?.id ?? null
    if (persistedIdRef.current === undefined) {
      persistedIdRef.current = nextId
      return
    }
    if (persistedIdRef.current === nextId) return
    persistedIdRef.current = nextId
    if (isAutomaticInitialSandboxRef.current) return
    copilotLocalStorage.setLastSelectedEnvironmentId(nextId)
  }, [selectedEnvironment])

  if (
    isEnvironmentSelectionStale(selectedEnvironment, availableEnvironments) ||
    (selectedEnvironment?.kind === 'sandbox' && !canHoldSandbox)
  ) {
    setSelectedEnvironmentState(null)
  }

  const isAhpEnvironment =
    isEnvironmentsEnabled && (selectedEnvironment?.kind === 'ahp' || selectedEnvironment?.kind === 'sandbox')
  const isSandboxEnvironment = isEnvironmentsEnabled && selectedEnvironment?.kind === 'sandbox'
  const showEnvironmentPicker = isEnvironmentsEnabled && !!onEnvironmentSubmit

  return {
    selectedEnvironment,
    setSelectedEnvironment,
    availableEnvironments,
    isAhpEnvironment,
    isSandboxEnvironment,
    showSandboxOption,
    showEnvironmentPicker,
    isEnvironmentsEnabled,
    isSandboxPolicyPending: sandboxPolicyPending,
  }
}

/**
 * Resolves the last-selected environment id from local storage to a live
 * option. Cloud resolves synchronously; Sandbox resolves only when the
 * mount can keep it (matching `useEnvironmentSelection`'s eligibility rules);
 * AHP ids are hydrated later by `useEnvironmentSelection` once the
 * environments list has loaded.
 */
export function resolvePersistedEnvironmentOption({
  supportsManagedSandbox = false,
  hasEnvironmentSubmit,
  deliversByHandoff = false,
}: {
  supportsManagedSandbox?: boolean
  hasEnvironmentSubmit: boolean
  deliversByHandoff?: boolean
}): EnvironmentOption | null {
  const id = copilotLocalStorage.getLastSelectedEnvironmentId()
  if (!id) return null
  if (id === CLOUD_ENVIRONMENT_OPTION.id) return CLOUD_ENVIRONMENT_OPTION
  if (id === SANDBOX_ENVIRONMENT_ID) {
    const canSelectSandbox =
      isSandboxOptionAvailable({
        isEnvironmentsEnabled: isFeatureEnabled('copilot_mission_control_environments_web_kickoff'),
        isManagedSandboxEnabled: isFeatureEnabled('copilot_mission_control_managed_sandbox_environments'),
        supportsManagedSandbox,
        hasEnvironmentSubmit,
      }) || deliversByHandoff
    return canSelectSandbox ? SANDBOX_ENVIRONMENT_OPTION : null
  }
  return null
}
